import { ERatePlanTax, ERatePlanTaxCapture } from "@payfac/types";

import type {
  IRatePlanTax,
  IRatePlanTaxCredit,
  IRatePlanTaxCreditMethod,
  IRatePlanTaxBankSlip,
  IRatePlanTaxDebit,
  IRatePlanTaxPix,
} from "@payfac/types";

export type TaxDiffFormatter =
  | "delay"
  | "percentage"
  | "currency"
  | "rateType"
  | "receiptType"
  | "text";

export type TaxDiffPrimitive = string | number | null;

export interface ITaxFieldDiff {
  fieldKey: string;
  labelKey: string;
  oldValue: TaxDiffPrimitive;
  newValue: TaxDiffPrimitive;
  formatter: TaxDiffFormatter;
}

export interface IInstallmentDiff {
  capture: string;
  brand: string;
  installment: number;
  oldValue: number;
  newValue: number;
}

export interface ICreditDiffSection {
  changes: ITaxFieldDiff[];
  installmentChanges: IInstallmentDiff[];
  totalChanges: number;
}

export interface IRatePlanTaxDiff {
  pix: { changes: ITaxFieldDiff[] };
  debit: { changes: ITaxFieldDiff[] };
  bankSlip: { changes: ITaxFieldDiff[] };
  credit: ICreditDiffSection;
  receiptType: ITaxFieldDiff | null;
  totalChanges: number;
  totalInstallmentsChanged: number;
  totalInstallments: number;
}

const isDifferent = (a: unknown, b: unknown): boolean => {
  if (a == null && b == null) return false;
  return a !== b;
};

const toDiffPrimitive = (value: unknown): TaxDiffPrimitive => {
  if (value == null) return null;
  if (typeof value === "string" || typeof value === "number") return value;
  return null;
};

const buildFieldDiff = (
  fieldKey: string,
  labelKey: string,
  oldValue: unknown,
  newValue: unknown,
  formatter: TaxDiffFormatter,
): ITaxFieldDiff | null => {
  if (!isDifferent(oldValue, newValue)) return null;

  return {
    fieldKey,
    labelKey,
    oldValue: toDiffPrimitive(oldValue),
    newValue: toDiffPrimitive(newValue),
    formatter,
  };
};

const PIX_CANDIDATES: Array<
  [keyof IRatePlanTaxPix, string, TaxDiffFormatter]
> = [
  ["delayForRedeem", "payfac.pageRatePlanModalRepassTimeLabel", "delay"],
  ["value", "payfac.pageRatePlanModalTotalChargedLabel", "percentage"],
  ["valueLimit", "payfac.pageRatePlanModalRateLimitLabel", "currency"],
  ["rateType", "payfac.pageRatePlanModalRateTypeLabel", "rateType"],
];

const DEBIT_CANDIDATES: Array<
  [keyof IRatePlanTaxDebit, string, TaxDiffFormatter]
> = [
  ["delayForRedeem", "payfac.pageRatePlanModalRepassTimeLabel", "delay"],
  ["value", "payfac.pageRatePlanModalTotalChargedLabel", "percentage"],
  ["rateType", "payfac.pageRatePlanModalRateTypeLabel", "rateType"],
];

const BANK_SLIP_CANDIDATES: Array<
  [keyof IRatePlanTaxBankSlip, string, TaxDiffFormatter]
> = [
  ["delayForRedeem", "payfac.pageRatePlanModalRepassTimeLabel", "delay"],
  ["value", "payfac.pageRatePlanModalTotalChargedLabel", "currency"],
  ["valueLimit", "payfac.pageRatePlanModalRateLimitLabel", "currency"],
  ["rateType", "payfac.pageRatePlanModalRateTypeLabel", "rateType"],
];

const diffSimpleMethod = <
  T extends IRatePlanTaxPix | IRatePlanTaxDebit | IRatePlanTaxBankSlip,
>(
  current: T | undefined,
  previous: T | undefined,
  candidates: Array<[keyof T & string, string, TaxDiffFormatter]>,
): ITaxFieldDiff[] => {
  const changes: ITaxFieldDiff[] = [];

  candidates.forEach(([field, labelKey, formatter]) => {
    const change = buildFieldDiff(
      field,
      labelKey,
      previous?.[field],
      current?.[field],
      formatter,
    );
    if (change) changes.push(change);
  });

  return changes;
};

const flattenCreditByCapture = (
  credit?: IRatePlanTaxCredit[],
): Record<string, IRatePlanTaxCreditMethod> => {
  const result: Record<string, IRatePlanTaxCreditMethod> = {};

  credit?.forEach((entry) => {
    Object.entries(entry).forEach(([capture, method]) => {
      if (!method) return;
      result[capture] = method;
    });
  });

  return result;
};

const CREDIT_CAPTURE_LABEL_KEY: Record<string, string> = {
  [ERatePlanTaxCapture.online]:
    "payfac.pageRatePlanDetailsRedeemTimeOnline",
  [ERatePlanTaxCapture.pos]: "payfac.pageRatePlanDetailsRedeemTimePos",
  [ERatePlanTaxCapture.tap]: "payfac.pageRatePlanDetailsRedeemTimeTap",
};

const getBrandRates = (
  method: IRatePlanTaxCreditMethod | undefined,
  brand: string,
): number[] | undefined => {
  if (!method) return undefined;

  const value = (method as Record<string, unknown>)[brand];
  if (!value || typeof value !== "object") return undefined;

  const rates = (value as { sellerRates?: unknown }).sellerRates;
  return Array.isArray(rates) ? (rates as number[]) : undefined;
};

const diffCredit = (
  current?: IRatePlanTaxCredit[],
  previous?: IRatePlanTaxCredit[],
): {
  delayChanges: ITaxFieldDiff[];
  installmentChanges: IInstallmentDiff[];
  totalInstallments: number;
} => {
  const currentByCapture = flattenCreditByCapture(current);
  const previousByCapture = flattenCreditByCapture(previous);

  const delayChanges: ITaxFieldDiff[] = [];
  const installmentChanges: IInstallmentDiff[] = [];
  let totalInstallments = 0;

  const captures = new Set<string>([
    ...Object.keys(currentByCapture),
    ...Object.keys(previousByCapture),
  ]);

  captures.forEach((capture) => {
    const cur = currentByCapture[capture];
    const prev = previousByCapture[capture];

    const delayChange = buildFieldDiff(
      `credit.${capture}.delayForRedeem`,
      CREDIT_CAPTURE_LABEL_KEY[capture] ??
        "payfac.pageRatePlanModalRepassTimeLabel",
      prev?.delayForRedeem,
      cur?.delayForRedeem,
      "delay",
    );
    if (delayChange) delayChanges.push(delayChange);

    const brands = new Set<string>([
      ...Object.keys(cur ?? {}).filter((k) => k !== "delayForRedeem"),
      ...Object.keys(prev ?? {}).filter((k) => k !== "delayForRedeem"),
    ]);

    brands.forEach((brand) => {
      const curRates = getBrandRates(cur, brand);
      const prevRates = getBrandRates(prev, brand);

      const length = Math.max(curRates?.length ?? 0, prevRates?.length ?? 0);
      totalInstallments += length;

      for (let index = 0; index < length; index += 1) {
        const oldValue = prevRates?.[index] ?? 0;
        const newValue = curRates?.[index] ?? 0;

        if (oldValue !== newValue) {
          installmentChanges.push({
            capture,
            brand,
            installment: index + 1,
            oldValue,
            newValue,
          });
        }
      }
    });
  });

  return { delayChanges, installmentChanges, totalInstallments };
};

const buildEmptyDiff = (): IRatePlanTaxDiff => ({
  pix: { changes: [] },
  debit: { changes: [] },
  bankSlip: { changes: [] },
  credit: { changes: [], installmentChanges: [], totalChanges: 0 },
  receiptType: null,
  totalChanges: 0,
  totalInstallmentsChanged: 0,
  totalInstallments: 0,
});

export const getRatePlanTaxDiff = (
  current: IRatePlanTax,
  previous?: IRatePlanTax | null,
): IRatePlanTaxDiff => {
  if (!previous) return buildEmptyDiff();

  const pixChanges = diffSimpleMethod(
    current[ERatePlanTax.pix],
    previous[ERatePlanTax.pix],
    PIX_CANDIDATES,
  );
  const debitChanges = diffSimpleMethod(
    current[ERatePlanTax.debit],
    previous[ERatePlanTax.debit],
    DEBIT_CANDIDATES,
  );
  const bankSlipChanges = diffSimpleMethod(
    current[ERatePlanTax.bankSlip],
    previous[ERatePlanTax.bankSlip],
    BANK_SLIP_CANDIDATES,
  );

  const credit = diffCredit(
    current[ERatePlanTax.credit],
    previous[ERatePlanTax.credit],
  );

  const creditTotalChanges =
    credit.delayChanges.length + credit.installmentChanges.length;

  const receiptType = buildFieldDiff(
    "receiptType",
    "payfac.pageRatePlanDetailsReceiptType",
    previous.receiptType,
    current.receiptType,
    "receiptType",
  );

  const totalChanges =
    pixChanges.length +
    debitChanges.length +
    bankSlipChanges.length +
    creditTotalChanges +
    (receiptType ? 1 : 0);

  return {
    pix: { changes: pixChanges },
    debit: { changes: debitChanges },
    bankSlip: { changes: bankSlipChanges },
    credit: {
      changes: credit.delayChanges,
      installmentChanges: credit.installmentChanges,
      totalChanges: creditTotalChanges,
    },
    receiptType,
    totalChanges,
    totalInstallmentsChanged: credit.installmentChanges.length,
    totalInstallments: credit.totalInstallments,
  };
};
